//================================================================
//  Tab: History 
//================================================================

//  Purpose: This tab displays the history of all approver reassignments

//  Example:
//    <History></History>    

//================================================================

//Libraries
import React, { useContext, useState, useEffect } from 'react';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components
import PageComponent from '../../../../../Components/PageComponent/PageComponent';

//Functions
import QueryListener from '../../../../../Library/QueryListener';
import ConvertDate from '../../../../../Library/ConvertDate';

//Images
import IconSearch from '../../../../../Components/Images/Icon_Search_Grey.svg';


export default function History() {

    //------------------------------------------------------
    //  useContext
    //------------------------------------------------------

    const getUser = useContext(GetUser);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState();

    // Search Input
    const [searchInput, setSearchInput] = useState('');

    // Used to store all History of the reassign approvers
    const [history, setHistory] = useState([]);

    // Used to store all History of the reassign approvers
    const [historyFiltered, setHistoryFiltered] = useState([]);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handles search function
    function handleSearch(value) {

        setSearchInput(value);

        // Reset the Filter
        if (value.length === 0) {

            setHistoryFiltered([...historyFiltered]);

        };

        // Search Filter
        //  1. Previous Approver
        //  2. Modified By
        //  3. New Approver
        //  4. Role

        const searchResults = history.filter((object) =>
            object?.previousapprover?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.actionedby?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.newapprover?.email?.toLowerCase().includes(value.toLowerCase()) ||
            object?.role?.toLowerCase().includes(value.toLowerCase())
        );

        // Save the filtered search results to the useState
        setHistoryFiltered(searchResults);

    };

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get History of the approvers reassign
    useEffect(() => {

        if (getUser === undefined) return;

        function onLoadChange(documents) {

            const historyData = documents.sort((a, b) => b.actioneddate - a.actioneddate);

            setHistory(historyData);
            setHistoryFiltered(historyData);

        }

        function onError(error) {

            console.log('error', error);
            setPageStatus('error-fatal');

        }

        // Filter by Admin type (Demand vs Commercial)
        // -- Note: Assumed people WON'T have both roles at ONCE!!!
        const query = getUser?.roles?.demandManagementAdmin === true ?  
            ['approvalbucket', 'not-in', ['Budget Owner (LoA) Approval', 'Finance Manager Approval']] : 
            ['approvalbucket', 'in', ['Budget Owner (LoA) Approval', 'Finance Manager Approval']] 
            
        const unsubscribe = QueryListener('approverhistory', [
            query
        ], onLoadChange, onLoadChange, onError);

        return () => {
            unsubscribe();
        };

    // eslint-disable-next-line
    }, [getUser])

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            body={
                <div className='flex flex-col px-[15px] gap-[20px]'>

                    {/* ----------------------------------- */}
                    {/* History Table                       */}
                    {/* ----------------------------------- */}

                    <div className='Table-Container'>

                        {/* ======== Sorting and Filters ========== */}
                        <div className='flex flex-row justify-between py-[10px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                            <label className='font-semibold self-center'>
                                Total: {historyFiltered?.length} of {history?.length}
                            </label>

                            {/* Search Bar */}
                            <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[300px]'>

                                <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                                <label htmlFor='searchInput'>
                                    <input
                                        id='searchInput'
                                        className='border-none h-[40px] p-0 m-0 outline-none'
                                        type='text'
                                        placeholder='Search'
                                        onChange={(e) => handleSearch(e.target.value)}
                                        value={searchInput}
                                    ></input>
                                </label>
                            </div>

                        </div>

                        <div className='overflow-x-auto overflow-y-auto rounded-b-lg min-h-[300px] max-h-[calc(100vh-400px)]'>
                         <table className='min-w-full table-auto'>
                            <thead>
                                <tr className='border-b-[#D8D8D8] border-solid border-b-2'>
                                    <th className='py-[15px] px-[20px] font-medium'>Previous Approver</th>
                                    <th className='py-[15px] px-[20px] font-medium'>New Approver</th>
                                    <th className='py-[15px] px-[20px] font-medium'>Role</th>
                                    <th className='py-[15px] px-[20px] font-medium'>Justification</th>
                                    <th className='py-[15px] px-[20px] font-medium'>Modified Date</th>
                                    <th className='py-[15px] px-[20px] font-medium'>Modified By</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    // No Historyfound
                                    historyFiltered.length === 0 ?
                                    (
                                        <tr>
                                            <td colSpan={6}>
                                                <div className='text-center p-[2%]'>
                                                    No records found.
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                    :
                                    (
                                        // Map in each record
                                        historyFiltered.map((history, index) => (
                                            <tr key={index} className='border-b-[#C8C8C9] border-solid border-b hover:bg-[#F0F7F7]'>

                                                {/* Previous Approver */}
                                                <td className='py-[15px] px-[20px]' >
                                                    <p className='font-medium mb-0'>{history?.previousapprover?.givenname} {history?.previousapprover?.surname}</p>
                                                    <p className='text-[#504f4f] mb-0 break-words'>{history?.previousapprover?.email}</p>
                                                </td>

                                                {/* New Approver */}
                                                <td className='py-[15px] px-[20px]' >
                                                <p className='font-medium mb-0'>{history?.newapprover?.givenname} {history?.newapprover?.surname}</p>
                                                <p className='text-[#504f4f] mb-0 break-words'>{history?.newapprover?.email}</p>
                                                </td>

                                                {/* Role*/}
                                                <td className='py-[15px] px-[20px]'>
                                                    {history?.role}
                                                </td>

                                                {/* Justification */}
                                                <td className='py-[15px] px-[20px] w-[300px]'>
                                                    {history?.justification}
                                                </td>

                                                {/* Modified Date */}
                                                <td className='py-[15px] px-[20px] w-[300px]'>{ConvertDate(history?.actioneddate)}</td>

                                                {/* Modified By */}
                                                <td className='py-[15px] px-[20px]'>
                                                    <p className='font-medium mb-0 capitalize'>{history?.actionedby?.givenname} {history?.actionedby?.surname}</p>
                                                    <p className='text-[#504f4f] mb-0 break-words'>{history?.actionedby?.email}</p>
                                                </td>

                                            </tr>
                                        ))
                                    )
                                }
                            </tbody>
                         </table>
                        </div>

                    </div>
                </div>
            }
        ></PageComponent>
    )
}